import { useCallback, useEffect, useRef, useState } from "react";
import debounce from "lodash/debounce";

export const useScrollMoreHook = () => {
  const scrollContainerRef = useRef<HTMLDivElement | null>(null);

  const resizeObserverRef = useRef<ResizeObserver>();
  const [isReachBottom, setIsReachBottom] = useState(false);
  const [isScrollVisible, setIsScrollVisible] = useState(false);

  const onContainerScroll = useDebounce(() => {
    if (scrollContainerRef.current) {
      const { scrollHeight, clientHeight, scrollTop } =
        scrollContainerRef.current;
      const scrollBottom = scrollHeight - clientHeight - scrollTop;
      setIsReachBottom(scrollBottom < 10);
    }
  });

  const onResize = useDebounce(() => {
    if (scrollContainerRef.current) {
      const { offsetHeight, scrollHeight } = scrollContainerRef.current;
      setIsScrollVisible(!!(offsetHeight < scrollHeight));

      onContainerScroll();
    }
  });

  useEffect(() => {
    const scrollContainer = scrollContainerRef.current;
    if (scrollContainer && ResizeObserver && !resizeObserverRef.current) {
      resizeObserverRef.current = new ResizeObserver(onResize);
      for (const el of scrollContainer.children || []) {
        resizeObserverRef.current?.observe(el);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scrollContainerRef.current]);

  const handleMoreButtonClick = () => {
    if (scrollContainerRef.current) {
      scrollContainerRef.current.scrollTop =
        scrollContainerRef.current.scrollHeight;
    }
  };

  const showMore = !isReachBottom && isScrollVisible;
  const scrollMoreElement = showMore ? (
    <div
      className="scroll-more-hint absolute bottom-[30px] left-[50px] z-[100] w-20 cursor-pointer rounded-[15px] bg-white p-[3px] text-center text-xs font-normal text-[#4F31FF] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#4F31FF] focus-visible:ring-offset-2"
      onClick={handleMoreButtonClick}
      role="button"
      tabIndex={0}
      aria-label="Scroll to see more menu items"
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          handleMoreButtonClick();
        }
      }}
    >
      More{" "}
      <i className="fa-solid fa-arrow-down text-[#4F31FF]" aria-hidden="true" />
    </div>
  ) : null;
  return {
    scrollContainerRef,
    scrollMoreElement,
    onContainerScroll,
  };
};

const useDebounce = (func: () => void) =>
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useCallback(debounce(func, 100), []);
